import { StateMachine } from "@/core/arch/state_machine";
import type { ReferralsRepository } from "./referrals_repository";
import type { ReferralStage, ReferredUserRow } from "./types";

export interface ReferralsState {
  items: ReferredUserRow[];
  status: "idle" | "loading" | "loaded" | "error";
  error?: string;
  nextToken: string | null;
  stageFilter: ReferralStage[];
}

export class ReferralsBloc extends StateMachine<ReferralsState> {
  private _loadToken = 0;

  constructor(private repo: ReferralsRepository) {
    super({
      items: [],
      status: "idle",
      nextToken: null,
      stageFilter: [],
    });
  }

  async load(): Promise<void> {
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading", error: undefined });

    try {
      const page = await this.repo.listReferredUsers({ limit: 500 });
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        items: page.items,
        nextToken: page.nextToken,
        status: "loaded",
        error: undefined,
      });
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load referrals",
      });
    }
  }

  async loadMore(): Promise<void> {
    if (!this.state.nextToken || this.state.status === "loading") return;
    const token = ++this._loadToken;
    this.setState({ ...this.state, status: "loading" });
    try {
      const page = await this.repo.listReferredUsers({
        nextToken: this.state.nextToken,
        limit: 500,
      });
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        items: [...this.state.items, ...page.items],
        nextToken: page.nextToken,
        status: "loaded",
      });
    } catch (err) {
      // eslint-disable-next-line security/detect-possible-timing-attacks -- request-sequencing counter
      if (token !== this._loadToken) return;
      this.setState({
        ...this.state,
        status: "error",
        error: err instanceof Error ? err.message : "Failed to load referrals",
      });
    }
  }

  setStageFilter(filter: ReferralStage[]): void {
    this.setState({ ...this.state, stageFilter: filter });
  }
}
